/**
 * Pooled table-crop annotator — labels live in Postgres, crops stream from S3.
 *
 * Differences from the older CropAnnotator (which edited a downloaded dataset copy):
 *   • no download step and no "upload a version" — every keypress lands in Postgres,
 *     per row, so several people can work the same zone at once;
 *   • the server pages the crops, so a zone with 3000+ of them opens instantly;
 *   • all counts come from the server as facets, so the chip counts, the per-table
 *     counts and the grid always agree.
 *
 * The funnel is a server-side STAGE: presence asks about every crop, in-frame only
 * about crops judged free, state only about crops judged free AND in frame. A crop the
 * previous stage has not judged is simply not in the next stage's queue — which is
 * also why an unset head must render as "не разм.", never as a real "свободно".
 *
 * Stickiness: labelling does NOT refetch. A crop you just labelled stays exactly where
 * it is (so a misclick is fixable on the spot); counts refresh on «Обновить», on a page
 * change, or when a filter changes.
 */
import { Select } from '@/components/ui/select';
import { useSearchParams } from 'react-router-dom';
import { unlinkCollection } from '@/api/collections';
import { CollectionRemoveDialog } from './CollectionFilter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  Loader2, Trash2, RefreshCw, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Play, Layers,
  ArrowDownUp,
} from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import {
  getPoolSamples, getPoolZones, getTrainStatus, poolAnnotate, poolDelete, poolRetrain,
  STAGES, STAGE_FIELDS, VIEW_ROWS, viewParams,
  type PoolItem, type PoolLabelDelta, type PoolSamples, type Sort, type Stage, type View,
} from '@/api/tablePool';
import { Button } from '@/components/ui/button';
import { layoutSafeLetter } from '@/utils/keyboardLayout';
import CollectCropsDialog from './CollectCropsDialog';
import { Tooltip } from '@/components/ui/tooltip';

const MIN_COLS = 2;
const MAX_COLS = 8;
const PAGE_SIZE = 200;
/** Binary stages label one field with Q(true)/W(false) and auto-advance. */
const BINARY_FIELD: Partial<Record<Stage, string>> = {
  presence: 'occupied', in_frame: 'table_in_frame',
};
const STATE_KEYS: { field: string; key: string; label: string }[] = [
  { field: 'native_objects_table', key: 'q', label: 'Нативные объекты' },
  { field: 'personal_items_table', key: 'w', label: 'Личные вещи на столе' },
  { field: 'food_dishes_table', key: 'e', label: 'Посуда с едой' },
  { field: 'dirty_dishes_table', key: 'r', label: 'Грязная посуда/мусор' },
];
type Labels = Record<string, boolean | null>;
/** i18n key for a view (the dictionary predates the BE's British spelling). */
const viewKey = (v: View) => (v === 'unlabelled' ? 'unlabeled' : v);

/**
 * Move the on-screen counts to match a save, WITHOUT refetching.
 *
 * Refetching is the obvious way to keep numbers honest, but it also pulls the page out
 * from under the labeller: a crop leaves the «Неразмеченные» queue the instant it is
 * judged, and a misclick has to stay fixable until they choose to reload. So the grid is
 * left exactly as it is and only the counters move.
 *
 * Each row is applied AT MOST ONCE per fetch (see `accounted`), which is what makes this
 * safe: `cur.items` still holds the server's original state, so re-editing a row cannot
 * decrement the same counter twice. Re-editing changes no count anyway — the row is by
 * then already "labelled by a human at this stage".
 */
export function applyLabelDelta(cur: PoolSamples, stage: Stage, view: View, ids: number[]): PoolSamples {
  const fields = STAGE_FIELDS[stage];
  const wanted = new Set(ids);
  const labelled = { ...cur.facets.labelled };
  const origins = { ...cur.facets.unlabelled_origins };
  const perTable = new Map(cur.facets.tables.map(r => [r.table_id, { ...r }]));
  let total = cur.total;

  for (const it of cur.items) {
    if (!wanted.has(it.id)) continue;
    // Both the verdict and the API's source belong to this selected stage.
    const wasLabelled = fields.every(f => it.labels[f] != null);
    let left = false;                       // did the row leave the CURRENT view?
    if (!wasLabelled) {
      labelled.unlabelled -= 1;
      origins[it.origin] = (origins[it.origin] ?? 0) - 1;
      labelled.all += 1;
      labelled.human += 1;
      left = view === 'unlabelled' || view === 'cancelled' || view === 'collected' || view === 'report_fix';
    } else if (it.source === 'ai') {
      labelled.ai -= 1;
      labelled.human += 1;
      left = view === 'ai';
    }
    if (!left) continue;                    // still matches the filter — counts only
    total -= 1;
    const row = perTable.get(it.table_id);
    if (row) row.count -= 1;
  }
  // `stage_total` and `zone_total` are deliberately untouched: no stage's gate depends on
  // the columns that same stage writes, so labelling never changes the size of the stage.
  return {
    ...cur,
    total,
    facets: {
      ...cur.facets, labelled, unlabelled_origins: origins,
      tables: [...perTable.values()],
    },
  };
}

export default function PooledTableAnnotator() {
  const { t } = useT();
  const [searchParams] = useSearchParams();
  const collection = searchParams.get('collection') || '';
  const [pendingRemoval, setPendingRemoval] = useState<PoolItem | null>(null);
  const [zoneId, setZoneId] = useState<number | null>(null);
  const [stage, setStage] = useState<Stage>('presence');
  // ONE selection, six views (see VIEW_ROWS): the unlabelled queue split by where the
  // crop came from, then the labelled one split by who labelled it. These used to be two
  // independent filters, which let you ask for "human-labelled AND cancelled-only" —
  // technically answerable, but not a question anybody means.
  const [view, setView] = useState<View>(() => {
    const requested = searchParams.get('view') as View;
    return VIEW_ROWS.flat().includes(requested) ? requested : 'unlabelled';
  });
  // Batch mode: N unlabelled crops from every zone, so a labeller never has to decide
  // what to work on next. 0 = off. `batchSize` is what the input holds; `batch` is what
  // is actually in force, so typing a number does not re-query on every keystroke.
  const [batchSize, setBatchSize] = useState(100);
  const [batch, setBatch] = useState(0);
  // Only offered on views that have dates. Sorting the unlabelled queue by annotation
  // date would order it by NULL, which is not an ordering.
  const [sort, setSort] = useState<Sort>('');
  const { labelled, origin } = viewParams(view);
  const [tableId, setTableId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [cols, setCols] = useState(3);
  const [focus, setFocus] = useState(0);
  const [saved, setSaved] = useState('');
  const [labels, setLabels] = useState<Record<number, Labels>>({});
  const [touched, setTouched] = useState<Set<number>>(new Set());
  const [removed, setRemoved] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  const { data: zonesResp, mutate: reloadZones } =
    useSWR('table-pool-zones', () => getPoolZones().then(r => r.data));
  const zones = zonesResp?.zones ?? [];
  useEffect(() => {
    if (!collection && zoneId == null && zones.length) setZoneId(zones[0].zone_id);
  }, [zones, zoneId, collection]);

  // What a retrain would train on right now. Revalidated after a run starts, and cheap
  // enough (two indexed COUNTs) to refetch when the labeller comes back to the tab.
  const { data: train, mutate: mutateTrain } = useSWR(
    'table-pool-train-status', () => getTrainStatus().then(r => r.data),
    { revalidateOnFocus: true });

  const key = (zoneId == null && !batch && !collection) ? null
    : ['table-pool', collection, batch ? 'batch' : zoneId, stage, view, batch, sort, tableId ?? 0, page] as const;
  const { data, isLoading, mutate } = useSWR(
    key,
    () => getPoolSamples({ collection, zone_id: zoneId, stage, labelled, origin, batch: collection ? 0 : batch, sort,
      table_id: batch ? null : tableId, page, page_size: PAGE_SIZE })
      .then(r => r.data),
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  // Seed local labels from the page. Rows already touched this session keep their local
  // value so a pending (debounced) edit is never overwritten by a stale server copy.
  // Keyed on the ITEMS array, not on `data`: the optimistic count updates below replace
  // `data` while keeping `items` by reference, and re-seeding on those would be pointless
  // work. Rows already touched this session keep their local value so a pending
  // (debounced) edit is never overwritten by a stale server copy.
  const rows = data?.items;
  useEffect(() => {
    if (!rows) return;
    setLabels(prev => {
      const next = { ...prev };
      for (const it of rows) if (!touched.has(it.id)) next[it.id] = { ...it.labels };
      return next;
    });
  }, [rows, touched]);
  // Focus belongs to the PAGE, not to the labels: it resets when the grid content
  // changes, NOT every time a row is first touched — that used to drag the keyboard
  // cursor back to the first card in the middle of a labelling run.
  const pageIds = rows?.map(it => it.id).join(',');
  useEffect(() => { setFocus(0); }, [pageIds]);

  /** Facet count for a view: the unlabelled row is split by crop provenance, the
   *  labelled row by who labelled it, so they read from two different facets. */
  const countFor = (v: View) =>
    (v === 'cancelled' || v === 'collected' || v === 'report_fix'
      ? data?.facets.unlabelled_origins?.[v]
      : data?.facets.labelled?.[v]) ?? 0;

  const items: PoolItem[] = useMemo(
    () => data?.stage === stage ? data.items.filter(i => !removed.has(i.id)) : [],
    [data, removed, stage]);

  // ── debounced save ─────────────────────────────────────────────────────────
  const dirty = useRef<Set<number>>(new Set());
  const labelsRef = useRef(labels); labelsRef.current = labels;
  const stageRef = useRef(stage); stageRef.current = stage;
  const viewRef = useRef(view); viewRef.current = view;
  // Rows whose transition has already been folded into the counts (per fetch).
  const accounted = useRef<Set<number>>(new Set());
  const timer = useRef<number | null>(null);
  const pendingSave = useRef<Promise<boolean>>(Promise.resolve(true));

  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (!dirty.current.size) return pendingSave.current;
    const ids = [...dirty.current];
    dirty.current.clear();
    const st = stageRef.current;
    const vw = viewRef.current;
    // Always send every column of the stage: a head left out would otherwise stay
    // NULL and the review would be lost (that is exactly how "clean" reviews used
    // to disappear).
    const payload: PoolLabelDelta[] = ids.map(id => {
      const l = labelsRef.current[id] || {};
      const row: PoolLabelDelta = { id };
      for (const f of STAGE_FIELDS[st]) row[f] = !!l[f];
      return row;
    });
    const save = pendingSave.current.then(async () => { try {
      const r = await poolAnnotate(st, payload);
      const accepted = r.data.items.map(it => it.id);
      const fresh = accepted.filter(id => !accounted.current.has(id));
      accepted.forEach(id => accounted.current.add(id));
      const provenance = new Map(r.data.items.map(it => [it.id, it]));
      await mutate(cur => {
        if (!cur) return cur;
        const next = applyLabelDelta(cur, st, vw, fresh);
        return { ...next, items: next.items.map(it => ({ ...it, ...provenance.get(it.id) })) };
      }, { revalidate: false });
      setSaved(`✓ ${r.data.saved}`);
      window.setTimeout(() => setSaved(s => (s.startsWith('✓') ? '' : s)), 1500);
      return true;
    } catch {
      setSaved('save failed');
      ids.forEach(i => dirty.current.add(i));
      return false;
    } });
    pendingSave.current = save;
    return save;
  }, [mutate]);

  const changeFilter = useCallback(async (fn: () => void) => {
    if (!await flush()) return;
    setTouched(new Set()); accounted.current.clear(); setPage(1); fn();
  }, [flush]);

  const scheduleSave = useCallback((id: number) => {
    dirty.current.add(id);
    if (timer.current) clearTimeout(timer.current);
    timer.current = window.setTimeout(flush, 250);
  }, [flush]);

  useEffect(() => () => { void flush(); }, [flush]);   // flush on unmount

  const setField = useCallback((it: PoolItem, field: string, value: boolean) => {
    setLabels(prev => ({ ...prev, [it.id]: { ...prev[it.id], [field]: value } }));
    setTouched(prev => (prev.has(it.id) ? prev : new Set(prev).add(it.id)));
    scheduleSave(it.id);
  }, [scheduleSave]);

  /** Mark the state stage reviewed — including an all-clean table (no flag pressed). */
  const markStateReviewed = useCallback((it: PoolItem) => {
    setLabels(prev => {
      const cur = prev[it.id] || {};
      const filled: Labels = { ...cur };
      for (const s of STATE_KEYS) filled[s.field] = !!cur[s.field];
      return { ...prev, [it.id]: filled };
    });
    setTouched(prev => (prev.has(it.id) ? prev : new Set(prev).add(it.id)));
    scheduleSave(it.id);
  }, [scheduleSave]);

  const advance = useCallback(() => {
    setFocus(f => Math.min(f + 1, Math.max(0, items.length - 1)));
  }, [items.length]);

  const move = useCallback((dir: 'left' | 'right' | 'up' | 'down') => {
    setFocus(f => {
      const n = items.length;
      if (!n) return 0;
      if (dir === 'right') return Math.min(n - 1, f + 1);
      if (dir === 'left') return Math.max(0, f - 1);
      if (dir === 'down') return Math.min(n - 1, f + cols);
      return Math.max(0, f - cols);
    });
  }, [items.length, cols]);

  // ── keyboard ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT') return;
      // Physical key, not the character: «й» and «q» are both KeyQ, so the shortcuts
      // work without the labeller switching layout. See utils/keyboardLayout.
      const k = layoutSafeLetter(e);
      if (e.key === 'ArrowRight') { e.preventDefault(); move('right'); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); move('left'); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); move('down'); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); move('up'); return; }
      if (e.key === 'Tab') {
        e.preventDefault();
        void changeFilter(() => setStage(STAGES[(STAGES.indexOf(stage) + 1) % STAGES.length]));
        return;
      }
      const it = items[focus];
      if (!it) return;
      const bf = BINARY_FIELD[stage];
      if (bf) {
        if (k === 'q') { e.preventDefault(); setField(it, bf, true); advance(); }
        else if (k === 'w') { e.preventDefault(); setField(it, bf, false); advance(); }
      } else {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); markStateReviewed(it); advance(); return; }
        const hit = STATE_KEYS.find(s => s.key === k);
        if (hit) {
          e.preventDefault();
          markStateReviewed(it);
          setField(it, hit.field, !labelsRef.current[it.id]?.[hit.field]);
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [stage, focus, items, move, advance, setField, markStateReviewed, changeFilter]);

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  useEffect(() => { cardRefs.current[focus]?.scrollIntoView({ block: 'center', behavior: 'smooth' }); },
    [focus, items]);

  const refresh = async () => {
    if (!await flush()) return;
    setTouched(new Set()); accounted.current.clear(); await mutate();
  };

  /** Train on the current state of the pool. Flush first so the last keypress counts. */
  const startRetrain = async () => {
    // Spell out what is about to be trained on: the button rents a GPU and eventually
    // moves a production model, so "N crops, K of them new" is the decision, not a detail.
    const lines = [t('data.pool.retrainConfirm')];
    if (train) {
      lines.push('', `${t('data.pool.trainOn')}: ${train.labelled_now}`);
      lines.push(train.new_since_last == null
        ? t('data.pool.trainFirstPooled')
        : `${t('data.pool.trainNew')}: +${train.new_since_last}`);
      if (train.unlabelled) lines.push(`${t('data.pool.trainSkipped')}: ${train.unlabelled}`);
    }
    if (!confirm(lines.join('\n'))) return;
    setBusy(true);
    try {
      await flush();
      const r = await poolRetrain();
      setSaved(`${t(r.data.already_running ? 'data.pool.retrainAlreadyRunning' : 'data.pool.retrainStarted')} ${r.data.run_id.slice(0, 8)}`);
      void mutateTrain();
      window.open(r.data.url, '_blank', 'noopener');
    } catch (e) {
      setSaved(`retrain failed: ${String((e as { message?: string })?.message ?? e).slice(0, 60)}`);
    } finally { setBusy(false); }
  };

  const removePhoto = async (it: PoolItem, action?: 'unlink' | 'delete') => {
    if (collection && collection !== 'none' && !action) { setPendingRemoval(it); return; }
    if (action !== 'unlink' && !confirm(t('data.annotate.deletePhotoConfirm'))) return;
    try {
      if (action === 'unlink') await unlinkCollection(collection, [it.id]);
      else await poolDelete([it.id]);
      setRemoved(prev => new Set(prev).add(it.id));
      setSaved(t(action === 'unlink' ? 'collection.unlinked' : 'data.annotate.photoDeleted'));
    } catch { setSaved('delete failed'); }
  };

  const zone = zones.find(z => z.zone_id === zoneId);
  const todo = zone ? (stage === 'presence' ? zone.todo_presence
    : stage === 'in_frame' ? zone.todo_in_frame : zone.todo_state) : 0;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / (data.page_size || PAGE_SIZE))) : 1;

  return (
    <div className="space-y-3">
      <CollectionRemoveDialog open={!!pendingRemoval} onClose={() => setPendingRemoval(null)} onChoose={action => {
        if (pendingRemoval) void removePhoto(pendingRemoval, action);
        setPendingRemoval(null);
      }} />
      {/* toolbar */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-md border bg-background/95 px-3 py-2 backdrop-blur">
        {/* Batch mode replaces the zone picker rather than sitting next to it: a batch
            spans every zone, so offering a zone filter alongside it would just let a
            labeller narrow their own assignment back down to the thing the batch
            exists to spare them. */}
        {batch ? (
          <span className="flex items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-2 py-1 text-sm">
            <Layers className="h-4 w-4" />
            {t('data.pool.batchActive')}: {data?.total ?? 0}
            <span className="text-xs text-muted-foreground">
              ({data?.facets.batch_zones?.length ?? 0} {t('data.pool.batchZones')} × ≤{batch})
            </span>
            <button className="text-xs underline" onClick={() => changeFilter(() => setBatch(0))}>
              {t('data.pool.batchExit')}
            </button>
          </span>
        ) : (
          <Select
            className="h-8" wrapperClassName="w-auto"
            value={zoneId ?? ''}
            onChange={e => { const v = e.target.value ? Number(e.target.value) : null; changeFilter(() => { setZoneId(v); setTableId(null); }); }}
          >
            {collection && <option value="">{t('collection.allZones')}</option>}
            {zones.map(z => (
              <option key={z.zone_id} value={z.zone_id}>📍 {z.zone} ({z.total})</option>
            ))}
          </Select>
        )}

        {/* Sits beside the zone picker and acts on whatever it points at. The picker
            lists configured-but-empty zones at (0) precisely so one can be selected and
            then filled here; a finished run reloads it so the (0) becomes a real count. */}
        {!batch && !collection && (
          <CollectCropsDialog
            zoneId={zoneId}
            zoneName={zone?.zone}
            onCollected={() => { void reloadZones(); }}
          />
        )}

        {!batch && !collection && (
          <Tooltip content={t('data.pool.batchHint')}><span className="flex items-center gap-1 text-xs">
            <input
              type="number" min={1} max={500} value={batchSize}
              className="w-16 rounded-md border bg-background px-1.5 py-1"
              onChange={e => setBatchSize(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
            />
            <Button variant="outline" size="sm"
                    onClick={() => changeFilter(() => { setBatch(batchSize); setTableId(null); })}>
              <Layers className="mr-1 h-4 w-4" /> {t('data.pool.batchStart')}
            </Button>
          </span></Tooltip>
        )}

        <div className="inline-flex overflow-hidden rounded-md border">
          {STAGES.map((m, idx) => (
            <button
              key={m}
              className={`px-3 py-1 text-sm ${stage === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
              onClick={() => changeFilter(() => setStage(m))}
            >
              {idx + 1}. {t(`data.annotate.mode.${m}`)}
            </button>
          ))}
        </div>

        <div className="inline-flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-7 w-7" title={t('data.annotate.zoomIn')}
                  disabled={cols <= MIN_COLS} onClick={() => setCols(c => Math.max(MIN_COLS, c - 1))}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <span className="w-4 text-center text-xs text-muted-foreground">{cols}</span>
          <Button variant="outline" size="icon" className="h-7 w-7" title={t('data.annotate.zoomOut')}
                  disabled={cols >= MAX_COLS} onClick={() => setCols(c => Math.min(MAX_COLS, c + 1))}>
            <ZoomOut className="h-4 w-4" />
          </Button>
        </div>

        {/* The view selector: unlabelled queue on top, labelled below. Every count is
            that view's OWN total — none is narrowed by the current selection, because a
            number that moves when you click elsewhere cannot be read as "work left". */}
        <div className="flex flex-col gap-1">
          {VIEW_ROWS.map((row, i) => (
            <div key={i} className="inline-flex overflow-hidden rounded-md border text-xs">
              {row.map(v => (
                <Tooltip key={v} content={v === 'cancelled' ? t('data.pool.cancelledHint') : v === 'report_fix' ? t('data.pool.reportFixHint') : undefined}><button
                  className={`px-2.5 py-1 ${view === v
                    ? (v === 'report_fix'
                      ? 'bg-rose-100 text-rose-900'
                      : v === 'cancelled'
                      ? 'bg-amber-100 text-amber-900'
                      : 'bg-secondary text-secondary-foreground')
                    : 'text-muted-foreground'}`}
                  onClick={() => changeFilter(() => setView(v))}
                >
                  {t(`data.annotate.filter.${viewKey(v)}`)} ({countFor(v)})
                </button></Tooltip>
              ))}
            </div>
          ))}
        </div>

        {/* Dates exist only once something has been labelled, so this appears with the
            labelled views and not on the unlabelled queue. */}
        {!batch && view !== 'unlabelled' && view !== 'cancelled' && view !== 'collected' && view !== 'report_fix' && (
          <Tooltip content={t('data.pool.sortHint')}><span className="flex items-center gap-1 rounded-md border px-2 py-1 text-xs">
            <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">{t('data.pool.sortLabel')}:</span>
            <Select
              wrapperClassName="w-auto"
              className={`h-auto border-0 bg-transparent py-0 pl-0 pr-6 text-xs shadow-none focus-visible:ring-0 ${sort ? 'font-medium text-primary' : ''}`}
              value={sort}
              onChange={e => { const v = e.target.value as Sort; changeFilter(() => setSort(v)); }}
            >
              <option value="">{t('data.pool.sortDefault')}</option>
              <option value="annotated_desc">{t('data.pool.sortNewest')}</option>
              <option value="annotated_asc">{t('data.pool.sortOldest')}</option>
            </Select>
          </span></Tooltip>
        )}

        {/* table filter — counts respect the selected chip */}
        <Select
          className="h-7 text-xs" wrapperClassName="w-auto"
          value={tableId ?? 'all'}
          title={t('data.annotate.tableFilter.label')}
          onChange={e => { const v = e.target.value; changeFilter(() => setTableId(v === 'all' ? null : Number(v))); }}
        >
          <option value="all">
            {t('data.annotate.tableFilter.all')} ({(data?.facets.tables ?? []).reduce((n, o) => n + o.count, 0)})
          </option>
          {(data?.facets.tables ?? []).map(o => (
            <option key={o.table_id} value={o.table_id}>🍽 {o.table} ({o.count})</option>
          ))}
        </Select>

        {BINARY_FIELD[stage] ? (
          <span className="text-xs text-muted-foreground">
            <kbd className="rounded bg-muted px-1">Q</kbd> {t(`data.annotate.bin.${stage}.yes`)} ·{' '}
            <kbd className="rounded bg-muted px-1">W</kbd> {t(`data.annotate.bin.${stage}.no`)} · ←→↑↓
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            {STATE_KEYS.map(s => (
              <span key={s.field} className="mr-2">
                <kbd className="rounded bg-muted px-1 uppercase">{s.key}</kbd> {s.label}
              </span>
            ))}
            · <kbd className="rounded bg-muted px-1">Space</kbd> {t('data.annotate.next')}
          </span>
        )}

        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-emerald-600">{saved}</span>
          <Tooltip content={t('data.pool.counterHint')}><span className="text-xs text-muted-foreground">
            {t('data.annotate.frame')} {items.length ? Math.min(focus + 1, items.length) : 0} / {items.length}
            {' · '}{t('data.pool.inFilter')} {data?.total ?? 0}
            {' · '}{t('data.annotate.left')} {todo}
          </span></Tooltip>
          {/* The funnel, spelled out: without "1615 из 3001" the stage-narrowed count
              looks like crops went missing. */}
          {data?.zone_total != null && (
            <Tooltip content={t(`data.pool.gate.${stage}`)}><span className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
              {t('data.pool.onStage')} {data.stage_total} {t('data.pool.outOf')} {data.zone_total}
            </span></Tooltip>
          )}
          <Button variant="outline" size="sm" onClick={refresh} title={t('data.pool.refreshHint')}>
            <RefreshCw className="mr-1 h-4 w-4" /> {t('data.pool.refresh')}
          </Button>
          {train && (
            <Tooltip content={t('data.pool.trainHint')}><span className="text-[11px] text-muted-foreground">
              {train.new_since_last == null
                ? `${train.labelled_now} ${t('data.pool.trainReady')}`
                : `+${train.new_since_last} ${t('data.pool.trainSinceLast')}`}
            </span></Tooltip>
          )}
          <Button size="sm" disabled={busy} onClick={startRetrain} title={t('data.pool.retrainHint')}>
            <Play className="mr-1 h-4 w-4" /> {t('data.pool.retrain')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <p className="text-xs text-muted-foreground">{t(`data.annotate.hint.${stage}`)}</p>
        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      {/* grid */}
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {items.map((it, i) => {
          const lab = labels[it.id] ?? {};
          const isFocus = i === focus;
          const activeFlags = STATE_KEYS.filter(s => lab[s.field]);
          const bf = BINARY_FIELD[stage];
          const raw = bf ? (lab[bf] as boolean | null | undefined) : undefined;
          let outline = 'outline outline-4 outline-slate-300';
          if (stage === 'in_frame') {
            outline = raw === false ? 'outline outline-4 outline-rose-500'
              : raw === true ? 'outline outline-4 outline-emerald-500' : outline;
          } else if (stage === 'presence') {
            if (raw === true) outline = 'outline outline-4 outline-emerald-500';
          } else if (activeFlags.length) {
            outline = 'outline outline-4 outline-amber-500';
          }
          return (
            <Tooltip key={it.id} content={`table ${it.table_id} · frame ${it.frame_id}`}><div
              ref={el => { cardRefs.current[i] = el; }}
              onClick={() => setFocus(i)}
              className={`group relative cursor-pointer rounded-lg bg-white p-1 ${outline} ${
                isFocus ? 'z-10 scale-[1.03] shadow-xl ring-[6px] ring-blue-600 ring-offset-2'
                  : 'opacity-95 hover:opacity-100'}`}
            >
              <Tooltip content={t('data.annotate.deletePhoto')}><button
                className="absolute left-1.5 top-1.5 z-10 rounded bg-rose-600/90 p-1 text-white opacity-0 transition group-hover:opacity-100 hover:bg-rose-700"
                aria-label={t('data.annotate.deletePhoto')}
                onClick={(e) => { e.stopPropagation(); removePhoto(it); }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button></Tooltip>
              <img src={it.crop_url} loading="lazy" alt={`t${it.table_id} f${it.frame_id}`}
                   className="block h-auto max-h-[600px] w-full rounded bg-black/5 object-contain" />
              <div className="mt-1 flex items-center justify-between gap-1 text-[11px] text-muted-foreground">
                <Tooltip content={it.camera ?? ''}><span className="truncate">📷 {it.camera ?? `cam ${it.camera_id ?? '?'}`}</span></Tooltip>
                <span className="flex shrink-0 items-center gap-1">
                  {bf && (raw === null || raw === undefined ? (
                    <span className="rounded bg-amber-100 px-1 font-semibold uppercase text-amber-800">
                      {t('data.annotate.unset')}
                    </span>
                  ) : (
                    <span className={`rounded px-1 font-semibold uppercase ${
                      stage === 'in_frame'
                        ? (raw ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800')
                        : (raw ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700')}`}>
                      {t(`data.annotate.bin.${stage}.${raw ? 'yes' : 'no'}`)}
                    </span>
                  ))}
                  {it.origin === 'report_fix' && (
                    <Tooltip content={t('data.pool.reportFixHint')}><span className="rounded bg-rose-100 px-1 font-semibold uppercase text-rose-900">
                      {t('data.pool.reportFixBadge')}
                    </span></Tooltip>
                  )}
                  {it.origin === 'cancelled' && (
                    // Visible even without the filter on: knowing a crop is a hard
                    // negative changes how carefully it is judged.
                    <Tooltip content={t('data.pool.cancelledHint')}><span className="rounded bg-amber-100 px-1 font-semibold uppercase text-amber-900">
                      {t('data.pool.cancelledBadge')}
                    </span></Tooltip>
                  )}
                  {it.zone && (
                    <Tooltip content={it.zone}><span className="truncate text-primary">📍 {it.zone}</span></Tooltip>
                  )}
                  <Tooltip content={it.table}><span className="truncate">🍽 {it.table}</span></Tooltip>
                </span>
              </div>
              <div className="mt-1 text-[11px] text-muted-foreground" data-testid="stage-provenance">
                <Tooltip content={[it.annotated_by, it.annotated_at && new Date(it.annotated_at).toLocaleString()].filter(Boolean).join(' · ')}>
                  <span>{t('data.pool.currentStageSource')}: {t(it.source === 'user'
                    ? 'data.annotate.humanBadge' : it.source === 'ai'
                      ? 'data.annotate.aiBadge' : 'data.annotate.unset')}</span>
                </Tooltip>
              </div>
              {stage === 'state' && (
                <div className="flex flex-wrap gap-0.5">
                  {activeFlags.map(s => (
                    <span key={s.field} className="rounded bg-amber-100 px-1 text-[10px] uppercase text-amber-800">
                      {s.key}
                    </span>
                  ))}
                  {!activeFlags.length && (
                    <span className="text-[10px] text-muted-foreground">
                      {STATE_KEYS.every(s => lab[s.field] === false) ? t('data.pool.cleanReviewed') : t('data.annotate.unset')}
                    </span>
                  )}
                </div>
              )}
            </div></Tooltip>
          );
        })}
      </div>

      {/* paging */}
      {data && data.total > (data.page_size || PAGE_SIZE) && (
        <div className="flex items-center justify-center gap-3 py-2">
          <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => void changeFilter(() => setPage(Math.max(1, page - 1)))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages}
                  onClick={() => void changeFilter(() => setPage(page + 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
      {!isLoading && !items.length && (
        <div className="py-10 text-center text-sm text-muted-foreground">{t('data.pool.empty')}</div>
      )}

    </div>
  );
}
